import { useState, useEffect, useCallback, useRef } from "react";
import { mailingApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { createBlock, textToBlocks, blocksToPlainText } from "@/components/mailing/emailBlocks";

// Kapselt State, Daten-Laden und alle Aktionen der Mailing-Seite.
export function useMailing() {
  const toast = useToast();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const fileInputRef = useRef(null);

  // Group dialog (create / rename)
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupEditMode, setGroupEditMode] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  // Contact dialog
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactSalutation, setContactSalutation] = useState("");
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");
  const [contactImportant, setContactImportant] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  // Send dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendSubject, setSendSubject] = useState("");
  const [sendBlocks, setSendBlocks] = useState([]);
  const [sendHero, setSendHero] = useState("");
  const [sendPreheader, setSendPreheader] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  // Templates (from API)
  const [templates, setTemplates] = useState([]);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);

  // Send history
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Email preview dialog
  const [previewHtmlOpen, setPreviewHtmlOpen] = useState(false);
  const [previewHtmlContent, setPreviewHtmlContent] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);

  // Import preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFilename, setPreviewFilename] = useState("");
  const [previewItems, setPreviewItems] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const selectedGroup = (Array.isArray(groups) ? groups : []).find((g) => g.id === selectedId) || null;

  const fetchGroups = useCallback(async () => {
    try {
      const res = await mailingApi.getGroups();
      const data = Array.isArray(res.data) ? res.data : [];
      setGroups(data);
      setSelectedId((prev) => {
        if (prev && data.some((g) => g.id === prev)) return prev;
        return data.length ? data[0].id : null;
      });
    } catch (error) {
      toast.error("Fehler beim Laden der Mailing-Gruppen");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await mailingApi.getTemplates();
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.debug("Vorlagen konnten nicht geladen werden (optional)", error);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ----- Group actions -----
  const openCreateGroup = () => {
    setGroupEditMode(false);
    setGroupName("");
    setGroupDialogOpen(true);
  };

  const openRenameGroup = () => {
    if (!selectedGroup) return;
    setGroupEditMode(true);
    setGroupName(selectedGroup.name);
    setGroupDialogOpen(true);
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    setSavingGroup(true);
    try {
      if (groupEditMode && selectedGroup) {
        await mailingApi.updateGroup(selectedGroup.id, groupName.trim());
        toast.success("Gruppe umbenannt");
      } else {
        const res = await mailingApi.createGroup(groupName.trim());
        setSelectedId(res.data.id);
        toast.success("Gruppe erstellt");
      }
      setGroupDialogOpen(false);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;
    try {
      await mailingApi.deleteGroup(groupToDelete.id);
      toast.success("Gruppe gelöscht");
      if (selectedId === groupToDelete.id) setSelectedId(null);
      fetchGroups();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    } finally {
      setGroupToDelete(null);
    }
  };

  // ----- Contact actions -----
  const openAddContact = () => {
    setEditContact(null);
    setContactName("");
    setContactEmail("");
    setContactSalutation("");
    setContactFirstName("");
    setContactLastName("");
    setContactImportant(false);
    setContactDialogOpen(true);
  };

  const openEditContact = (contact) => {
    setEditContact(contact);
    setContactName(contact.name || "");
    setContactEmail(contact.email || "");
    setContactSalutation(contact.salutation || "");
    setContactFirstName(contact.first_name || "");
    setContactLastName(contact.last_name || "");
    setContactImportant(!!contact.is_important);
    setContactDialogOpen(true);
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();
    if (!selectedGroup) return;
    setSavingContact(true);
    try {
      const data = {
        name: contactName.trim(),
        email: contactEmail.trim(),
        salutation: contactSalutation,
        first_name: contactFirstName.trim(),
        last_name: contactLastName.trim(),
        is_important: contactImportant,
      };
      if (editContact) {
        await mailingApi.updateContact(selectedGroup.id, editContact.id, data);
        toast.success("Kontakt aktualisiert");
      } else {
        await mailingApi.addContact(selectedGroup.id, data);
        toast.success("Kontakt hinzugefügt");
      }
      setContactDialogOpen(false);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedGroup || !contactToDelete) return;
    try {
      await mailingApi.deleteContact(selectedGroup.id, contactToDelete.id);
      toast.success("Kontakt entfernt");
      fetchGroups();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    } finally {
      setContactToDelete(null);
    }
  };

  // ----- Import (preview then confirm) -----
  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selectedGroup) return;
    setImporting(true);
    try {
      const res = await mailingApi.previewImport(selectedGroup.id, file);
      const items = (res.data.contacts || []).map((c) => ({ ...c, selected: !c.exists }));
      setPreviewFilename(res.data.filename || file.name);
      setPreviewItems(items);
      setPreviewOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setImporting(false);
    }
  };

  const togglePreviewItem = (email) => {
    setPreviewItems((prev) => prev.map((it) => (it.email === email ? { ...it, selected: !it.selected } : it)));
  };

  const previewSelectedCount = previewItems.filter((it) => it.selected).length;
  const allNewSelected = previewItems.length > 0 && previewItems.every((it) => it.selected);

  const toggleSelectAll = () => {
    const next = !allNewSelected;
    setPreviewItems((prev) => prev.map((it) => ({ ...it, selected: next })));
  };

  const closeImportPreview = () => {
    setPreviewOpen(false);
    setPreviewItems([]);
  };

  const handleConfirmImport = async () => {
    if (!selectedGroup) return;
    const chosen = previewItems.filter((it) => it.selected).map(({ name, email }) => ({ name, email }));
    if (chosen.length === 0) {
      toast.error("Bitte mindestens einen Kontakt auswählen");
      return;
    }
    setConfirming(true);
    try {
      const res = await mailingApi.confirmImport(selectedGroup.id, previewFilename, chosen);
      const { added, skipped } = res.data;
      toast.success(`${added} importiert${skipped ? `, ${skipped} übersprungen (Duplikate)` : ""}`);
      setPreviewOpen(false);
      setPreviewItems([]);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setConfirming(false);
    }
  };

  // ----- Send -----
  const openSend = () => {
    setSendSubject("");
    setSendBlocks([createBlock("text")]);
    setSendHero("");
    setSendPreheader("");
    setSelectedTemplate("");
    setSendDialogOpen(true);
  };

  const applyTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    const tpl = (Array.isArray(templates) ? templates : []).find((t) => t.id === templateId);
    if (tpl) {
      setSendSubject(tpl.subject || "");
      setSendHero(tpl.hero_image || "");
      setSendPreheader(tpl.preheader || "");
      if (tpl.content_type === "blocks" && Array.isArray(tpl.blocks) && tpl.blocks.length) {
        setSendBlocks(tpl.blocks.map((b) => ({ ...b })));
      } else {
        setSendBlocks(textToBlocks(tpl.body || ""));
      }
    }
  };

  const buildPayload = () => ({
    subject: sendSubject.trim(),
    content_type: "blocks",
    blocks: sendBlocks,
    message: blocksToPlainText(sendBlocks),
    hero_image: sendHero || null,
    preheader: sendPreheader || null,
    template_id: selectedTemplate || null,
  });

  const hasContent = blocksToPlainText(sendBlocks).trim().length > 0 || sendBlocks.some((b) => b.type === "image" && b.src);

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const res = await mailingApi.sendTest(buildPayload());
      toast.success(`Testmail an ${res.data.email} gesendet`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Testversand fehlgeschlagen");
    } finally {
      setSendingTest(false);
    }
  };

  const openHistory = async () => {
    if (!selectedGroup) return;
    setHistoryOpen(true);
    setLoadingHistory(true);
    try {
      const res = await mailingApi.getHistory(selectedGroup.id);
      setHistory(res.data);
    } catch (error) {
      toast.error("Verlauf konnte nicht geladen werden");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handlePreviewHtml = async () => {
    setLoadingPreview(true);
    try {
      const res = await mailingApi.previewHtml(buildPayload());
      setPreviewHtmlContent(res.data.html);
      setPreviewHtmlOpen(true);
    } catch (error) {
      toast.error("Vorschau konnte nicht geladen werden");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedGroup) return;
    setSending(true);
    try {
      const res = await mailingApi.send(selectedGroup.id, buildPayload());
      toast.success(`Rundmail an ${res.data.recipients} Empfänger gesendet`);
      setSendDialogOpen(false);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  const contacts = selectedGroup?.contacts || [];

  return {
    groups, loading, selectedId, setSelectedId, selectedGroup, fileInputRef, contacts,
    groupDialogOpen, setGroupDialogOpen, groupEditMode, groupName, setGroupName, savingGroup,
    groupToDelete, setGroupToDelete,
    contactDialogOpen, setContactDialogOpen, editContact, contactName, setContactName,
    contactEmail, setContactEmail, savingContact, contactToDelete, setContactToDelete,
    contactSalutation, setContactSalutation, contactFirstName, setContactFirstName,
    contactLastName, setContactLastName, contactImportant, setContactImportant,
    sendDialogOpen, setSendDialogOpen, sendSubject, setSendSubject, sendBlocks, setSendBlocks,
    sendHero, setSendHero, sendPreheader, setSendPreheader,
    hasContent,
    sending, selectedTemplate, sendingTest,
    templates, templateManagerOpen, setTemplateManagerOpen, fetchTemplates,
    historyOpen, setHistoryOpen, history, loadingHistory,
    previewHtmlOpen, setPreviewHtmlOpen, previewHtmlContent, loadingPreview, importing,
    previewOpen, previewFilename, previewItems, confirming,
    previewSelectedCount, allNewSelected,
    openCreateGroup, openRenameGroup, handleSaveGroup, handleDeleteGroup,
    openAddContact, openEditContact, handleSaveContact, handleDeleteContact,
    handleImportClick, handleImportFile, togglePreviewItem, toggleSelectAll,
    closeImportPreview, handleConfirmImport,
    openSend, applyTemplate, handleSendTest, openHistory, handlePreviewHtml, handleSend,
  };
}
